// =============================================================================
// Revision Timeline — post-order design-edit history (admin)
// =============================================================================
// Renders the order's revision history (newest first): revision number, why it
// was created (initial export / regenerate / design edit), who triggered it,
// the per-edit change reason, and a per-revision print-file ZIP download.
// Distinct from EventTimeline (generic lifecycle) so design revisions get their
// own scannable section. Admin downloads are NOT audience-filtered (full files).
// =============================================================================

import { Badge } from '@tkpod/ui';

export interface RevisionItemEntry {
  orderItemId: string;
  /** True when this revision item has a captured composite thumbnail. */
  hasPreview: boolean;
  /** Live OrderItem product title (distinguishes lines in multi-item orders). */
  title?: string | null;
  /** Live OrderItem variant label, when the product has variants. */
  variantTitle?: string | null;
  /** Sheet partKey for gangsheet/DTF lines (null for apparel/legacy). */
  partKey?: string | null;
  /**
   * Mode-aware line label: "Sheet N" for gangsheet/DTF (every sheet shares
   * title·variantTitle), the variant title for apparel. Distinguishes lines.
   */
  lineLabel?: string | null;
  /**
   * True when this line owns a single downloadable print file for this revision
   * (gangsheet / DTF). Admin is always allowed to download.
   */
  hasPrint?: boolean;
  /** Print (sheet) width in inches for this revision, when a single print. */
  printWidthIn?: number | null;
  /** Print (sheet) height in inches for this revision, when a single print. */
  printHeightIn?: number | null;
}

/** Format an inch value: drop trailing zeros (22, 23.5) for a compact label. */
function formatInches(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
}

/** "22 × 24 in" when both dims are valid, else null. */
function formatPrintSize(
  widthIn?: number | null,
  heightIn?: number | null,
): string | null {
  if (
    typeof widthIn !== 'number' ||
    typeof heightIn !== 'number' ||
    widthIn <= 0 ||
    heightIn <= 0
  ) {
    return null;
  }
  return `${formatInches(widthIn)} × ${formatInches(heightIn)} in`;
}

export interface RevisionEntry {
  revision: number;
  reason: string;
  status: string;
  createdAt: string;
  triggeredByName: string | null;
  changeReason: string | null;
  changedByType: string | null;
  changedByName: string | null;
  itemCount: number;
  fileCount: number;
  hasFiles: boolean;
  /** Revision-scoped order-summary PDF is rendered and downloadable. */
  hasSummaryPdf?: boolean;
  items?: RevisionItemEntry[];
}

interface RevisionTimelineProps {
  revisions: RevisionEntry[];
  orderId: string;
  /** API origin for the per-revision ZIP download link. */
  apiBase: string;
}

const REASON_LABELS: Record<string, string> = {
  INITIAL: 'Initial export',
  REGENERATE: 'Regenerated',
  DESIGN_EDITED: 'Design edited',
  ARTWORK_REPLACED: 'File replaced',
};

const REASON_VARIANT: Record<
  string,
  'primary' | 'default' | 'success' | 'warning' | 'danger' | 'info'
> = {
  INITIAL: 'default',
  REGENERATE: 'info',
  DESIGN_EDITED: 'primary',
  ARTWORK_REPLACED: 'primary',
};

const STATUS_VARIANT: Record<
  string,
  'primary' | 'default' | 'success' | 'warning' | 'danger' | 'info'
> = {
  PENDING: 'warning',
  PROCESSING: 'warning',
  COMPLETED: 'success',
  FAILED: 'danger',
};

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })} at ${d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })}`;
}

export function RevisionTimeline({
  revisions,
  orderId,
  apiBase,
}: RevisionTimelineProps) {
  if (revisions.length === 0) {
    return (
      <p className="py-3 text-center text-xs text-text-tertiary">
        No revisions yet.
      </p>
    );
  }

  const downloadIcon = (
    <svg
      className="h-3.5 w-3.5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
      />
    </svg>
  );

  return (
    <div className="relative space-y-3 pl-4">
      <div className="absolute left-[5px] top-1 bottom-1 w-px bg-border" />
      {revisions.map((rev) => {
        const actor =
          rev.changedByName ??
          rev.triggeredByName ??
          (rev.changedByType
            ? rev.changedByType === 'ADMIN'
              ? 'Transfer Kingdom'
              : 'Merchant'
            : null);
        return (
          <div key={rev.revision} className="relative">
            <div className="absolute -left-4 top-[5px] h-[7px] w-[7px] rounded-full border-2 border-border bg-surface" />
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs font-semibold text-text-primary">
                  Revision {rev.revision}
                </span>
                <Badge variant={REASON_VARIANT[rev.reason] ?? 'default'} size="sm">
                  {REASON_LABELS[rev.reason] ?? rev.reason.replace(/_/g, ' ')}
                </Badge>
                {rev.status !== 'COMPLETED' && (
                  <Badge
                    variant={STATUS_VARIANT[rev.status] ?? 'default'}
                    size="sm"
                  >
                    {rev.status.replace(/_/g, ' ')}
                  </Badge>
                )}
                <span
                  className="text-[10px] text-text-tertiary"
                  title={rev.createdAt}
                >
                  {formatTimestamp(rev.createdAt)}
                </span>
              </div>

              {rev.changeReason && (
                <p className="text-[11px] text-text-secondary">
                  <span className="text-text-tertiary">Reason:</span>{' '}
                  {rev.changeReason}
                </p>
              )}
              <p className="text-[10px] text-text-tertiary">
                {rev.itemCount} item{rev.itemCount === 1 ? '' : 's'}
                {actor ? ` · by ${actor}` : ''}
              </p>

              {(() => {
                const lines = (rev.items ?? []).filter(
                  (it) => it.hasPreview || it.hasPrint,
                );
                if (lines.length === 0) return null;
                return (
                  <div className="mt-1 space-y-1.5">
                    {lines.map((it) => {
                      // Prefer the mode-aware label (Sheet N / variant) so the
                      // sheets of a gangsheet are distinguishable; fall back to
                      // the raw variant title for legacy rows without a label.
                      const sublabel = it.lineLabel ?? it.variantTitle;
                      const label = [it.title, sublabel]
                        .filter(Boolean)
                        .join(' · ');
                      const printSize = formatPrintSize(
                        it.printWidthIn,
                        it.printHeightIn,
                      );
                      return (
                        <div
                          key={it.orderItemId}
                          className="flex items-center gap-2"
                        >
                          {it.hasPreview ? (
                            <a
                              href={`${apiBase}/api/v1/admin/orders/${orderId}/revisions/${rev.revision}/items/${it.orderItemId}/preview.png`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Open full preview"
                              className="shrink-0 cursor-zoom-in rounded-md transition-opacity hover:opacity-80"
                            >
                              <img
                                src={`${apiBase}/api/v1/admin/orders/${orderId}/revisions/${rev.revision}/items/${it.orderItemId}/preview.png`}
                                alt={`Revision ${rev.revision} preview`}
                                loading="lazy"
                                className="h-14 w-14 rounded-md border border-border bg-surface-secondary object-contain"
                              />
                            </a>
                          ) : (
                            <div className="h-14 w-14 shrink-0 rounded-md border border-dashed border-border bg-surface-secondary" />
                          )}
                          <div className="min-w-0 flex-1 space-y-0.5">
                            {label && (
                              <p
                                className="truncate text-[11px] font-medium text-text-primary"
                                title={label}
                              >
                                {label}
                              </p>
                            )}
                            {printSize && (
                              <p className="text-[10px] text-text-tertiary tabular-nums">
                                {printSize}
                              </p>
                            )}
                            {it.hasPrint && (
                              <a
                                href={`${apiBase}/api/v1/admin/orders/${orderId}/revisions/${rev.revision}/items/${it.orderItemId}/print.png`}
                                className="inline-flex items-center gap-1 text-[11px] font-medium text-accent hover:underline"
                              >
                                {downloadIcon}
                                Print file
                              </a>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              <div className="flex flex-wrap items-center gap-2">
                {rev.hasFiles && (
                  <a
                    href={`${apiBase}/api/v1/admin/orders/${orderId}/revisions/${rev.revision}/export.zip`}
                    className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1 text-[11px] font-medium text-text-primary transition-colors hover:bg-surface-secondary"
                  >
                    {downloadIcon}
                    Download files (ZIP)
                  </a>
                )}
                {rev.hasSummaryPdf && (
                  <a
                    href={`${apiBase}/api/v1/admin/orders/${orderId}/revisions/${rev.revision}/summary.pdf`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1 text-[11px] font-medium text-text-primary transition-colors hover:bg-surface-secondary"
                  >
                    {downloadIcon}
                    Summary (PDF)
                  </a>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
